import {
  SHORT_URL_FLOWS,
  SHORT_URL_RENDER_STATES,
  SHORT_URL_SOURCES,
  ShortUrlFlow,
  ShortUrlRenderState,
  ShortUrlSource,
} from "@/modules/tracking/consts/ShortUrl";

const STORAGE_KEY = "short_url_flow_session";
const SCHEMA_VERSION = 1;

export type ShortUrlFlowSessionPayload = {
  version: number;
  flow: ShortUrlFlow;
  accessCode: string;
  startedAt: number;
  lastScreen: ShortUrlRenderState | null;
  sessionId: string | null;
  source: ShortUrlSource;
  correlationId: string | null;
};

const isBrowser = typeof window !== "undefined";

const getStore = (): Storage | null => {
  if (!isBrowser) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isValidFlow = (value: unknown): value is ShortUrlFlow =>
  typeof value === "string" &&
  (SHORT_URL_FLOWS as readonly string[]).includes(value);

const isValidSource = (value: unknown): value is ShortUrlSource =>
  typeof value === "string" &&
  (SHORT_URL_SOURCES as readonly string[]).includes(value);

const isValidLastScreen = (
  value: unknown,
): value is ShortUrlRenderState | null =>
  value === null ||
  (typeof value === "string" &&
    (SHORT_URL_RENDER_STATES as readonly string[]).includes(value));

const isShortUrlFlowSessionPayload = (
  value: unknown,
): value is ShortUrlFlowSessionPayload => {
  if (value === null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.version === SCHEMA_VERSION &&
    isValidFlow(v.flow) &&
    typeof v.accessCode === "string" &&
    typeof v.startedAt === "number" &&
    Number.isFinite(v.startedAt) &&
    isValidLastScreen(v.lastScreen) &&
    (v.sessionId === null || typeof v.sessionId === "string") &&
    isValidSource(v.source) &&
    (v.correlationId === null || typeof v.correlationId === "string")
  );
};

export function startShortUrlFlowSession(
  payload: Omit<
    ShortUrlFlowSessionPayload,
    "startedAt" | "lastScreen" | "version"
  > & {
    lastScreen?: ShortUrlRenderState | null;
  },
): void {
  const store = getStore();
  if (!store) return;
  const value: ShortUrlFlowSessionPayload = {
    version: SCHEMA_VERSION,
    flow: payload.flow,
    accessCode: payload.accessCode,
    sessionId: payload.sessionId,
    source: payload.source,
    correlationId: payload.correlationId,
    startedAt: Date.now(),
    lastScreen: payload.lastScreen ?? null,
  };
  store.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function getShortUrlFlowSession(): ShortUrlFlowSessionPayload | null {
  const store = getStore();
  if (!store) return null;
  const raw = store.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isShortUrlFlowSessionPayload(parsed)) {
      // Self-heal: stale or malformed payload from a previous schema or
      // manual tampering. Drop it so downstream consumers don't emit
      // events with `undefined` fields.
      store.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    store.removeItem(STORAGE_KEY);
    return null;
  }
}

export function updateShortUrlFlowScreen(screen: ShortUrlRenderState): void {
  const current = getShortUrlFlowSession();
  if (!current) return;
  const store = getStore();
  if (!store) return;
  store.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...current, lastScreen: screen }),
  );
}

export function endShortUrlFlowSession(): void {
  const store = getStore();
  if (!store) return;
  store.removeItem(STORAGE_KEY);
}

export function timeInFlowSeconds(startedAt: number): number {
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

export function isSameShortUrlFlowVisit(
  flow: ShortUrlFlow,
  accessCode: string,
): boolean {
  const session = getShortUrlFlowSession();
  return session?.flow === flow && session.accessCode === accessCode;
}
